import type { ReactElement } from 'react';
import { useEffect, useState } from 'react';
import { datadogRum } from '@datadog/browser-rum';
import { InView } from 'react-intersection-observer';
import reportEligibility from './utils/reportEligibility';
import reportViewability from './utils/reportViewability';
import type { AdButlerAdPlacement, AdButlerApiResponse } from './types';
import { ExternalLink } from '@fastnorth/kraken/components/navigation';
import { Image } from '@fastnorth/kraken/components/image';

type AdButlerBlockProps = {
  eventId: number;
  url: string; // this url is what AdButler initially provides to the block
  className?: string;
};

const AdButlerBlock = ({
  url,
  eventId,
  className: devClassName,
}: AdButlerBlockProps): ReactElement | null => {
  const [adProps, setAdProps] = useState<AdButlerAdPlacement | null>(null);

  useEffect(() => {
    if (url !== '' && eventId) {
      fetch(`${url};kw=event_${eventId}`)
        .then((res) => res.json())
        .then((data: AdButlerApiResponse) => {
          if (data.status === 'SUCCESS') {
            setAdProps(data.placements.placement_1);
            reportEligibility(data);
          }
        })
        .catch((error) => {
          if (error?.request?.status === 0) {
            datadogRum.addAction('AdButler blocked', {
              ...error,
              message: `${error.message} - AdButler blocked`,
            });
            return;
          }
          datadogRum.addError(new Error('Error fetching AdButler ad'), {
            ...error,
            message: `${error.message} - Error fetching AdButler ad`,
          });
          return;
        });
    }
  }, [url, eventId]);

  if (!adProps || url === '' || !eventId) {
    return null;
  }

  // if the AdButler response contains an HTML block, render that
  if (adProps?.body) {
    return (
      <InView
        as="div"
        threshold={0.5}
        triggerOnce={true}
        className={devClassName}
        onChange={(inView) => {
          if (inView) {
            reportViewability(adProps.viewable_url);
          }
        }}>
        <div dangerouslySetInnerHTML={{ __html: adProps.body }} />
      </InView>
    );
  }

  // render the normal AdButler ad
  return (
    <InView
      as="div"
      threshold={0.5}
      triggerOnce={true}
      className={devClassName}
      onChange={(inView) => {
        if (inView) {
          reportViewability(adProps.viewable_url);
        }
      }}>
      <ExternalLink href={adProps.redirect_url} title={adProps.alt_text} target={adProps.target}>
        <Image src={adProps.image_url} alt={adProps.alt_text} />
      </ExternalLink>
    </InView>
  );
};

export default AdButlerBlock;
